'use client';

import { rockyTypeToMXType } from '@util/index';
import { SearchIcon } from 'lucide-react';
import { type Make } from 'models/shared';
import { useState } from 'react';
import { useCreateListing } from '../_context';
import {
  useVehicleSearch,
  type VehicleSearchResult,
} from '../_hooks/useVehicleSearch';

export default function FindVehicle() {
  const { form, goToStep } = useCreateListing();
  const [query, setQuery] = useState('');
  const { results, isLoading, extractedYear, hasQuery } =
    useVehicleSearch(query);

  const handleSelect = (result: VehicleSearchResult) => {
    const mxType = rockyTypeToMXType(result.type);
    // TODO: wire the full vehicle category mapping. For now we only seed
    // `ride_types` and a placeholder `category` so the create-listing form has
    // a starting point — the remaining fields are wired up in a follow-up.
    form.setValue('ride_types', mxType ? [mxType] : []);
    form.setValue('category', mxType ?? '');
    // Pre-fill the bike spec fields from the chosen vehicle. Bike Type and
    // Engine Size aren't part of the vehicle record, so the seller still picks
    // those. Year falls back to the most recent year the model was made for
    // when not extracted from the query — without a year set, ProductInfo's
    // Model Select renders an empty option list, so we'd end up showing only
    // Make pre-filled.
    const availableYears = result.hit.document.years ?? [];
    const year =
      extractedYear ??
      (availableYears.length > 0 ? Math.max(...availableYears) : null);
    form.setValue('make', result.make as Make);
    form.setValue('model', result.model);
    if (year) form.setValue('year', year);
    // Brand for vehicles == make; we hide the Brand picker for vehicles in
    // ProductInfo, so seed it here from the chosen vehicle.
    form.setValue('brand', result.make);
    const title = year
      ? `${year} ${result.make} ${result.model}`
      : `${result.make} ${result.model}`;
    form.setValue('title', title);
    goToStep('create-listing');
  };

  const showEmptyState = hasQuery && !isLoading && results.length === 0;

  return (
    <div className="mx-auto my-3 flex max-w-[100rem] flex-col gap-[2rem] px-[2rem] sm:my-[8rem]">
      <h1 className="self-start text-[3rem] font-bold">Find your vehicle</h1>

      <div className="relative flex w-full overflow-hidden rounded-[1.2rem] border border-gray-600 bg-white">
        <input
          placeholder='Try searching "2024 Yamaha YZ250F"'
          className="relative h-[5.5rem] grow border-none bg-white px-[2rem] text-[2rem] outline-none"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <SearchIcon
          fill="white"
          className="absolute right-[2rem] top-[50%] translate-y-[-50%] bg-white"
        />
      </div>

      {isLoading && (
        <div className="flex flex-col gap-[1rem]">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[7rem] w-full animate-pulse rounded-[1.2rem] bg-gray-200"
            />
          ))}
        </div>
      )}

      {!isLoading && results.length > 0 && (
        <div className="flex flex-col gap-[0.5rem]">
          {results.map((result) => {
            const label = extractedYear
              ? `${extractedYear} ${result.make} ${result.model}`
              : `${result.make} ${result.model}`;
            return (
              <button
                key={`${result.make}-${result.model}-${result.type}`}
                type="button"
                onClick={() => handleSelect(result)}
                className="flex w-full items-center rounded-[1.2rem] px-[2rem] py-[1.75rem] text-left text-[2rem] font-medium capitalize text-black transition-colors hover:bg-gray-100"
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {showEmptyState && (
        <p className="text-[2rem] font-medium text-gray-500">
          No vehicles match &quot;{query.trim()}&quot;. Try another year, make
          or model.
        </p>
      )}

      <div className="mt-16 flex w-full justify-center">
        <button
          className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-lightest-gray px-6 py-3 text-[1.6rem] text-black hover:bg-brand-light-gray sm:grow-0 sm:px-8 sm:py-4 sm:text-[1.8rem]"
          type="button"
          onClick={() => goToStep('select-listing-type')}
        >
          Back
        </button>
      </div>
    </div>
  );
}
